import { Link } from 'react-router-dom';
import { Play, Pause } from 'lucide-react';
import { useState, useRef } from 'react';
import logo from '@/assets/logo.png';
import logoFoxBusiness from '@/assets/logo-fox-business-new.png';
import logoCnbc from '@/assets/logo-cnbc-new.svg';
import logoOnbe from '@/assets/logo-onbe-new.webp';
import logoAfn from '@/assets/logo-afn-new2.png';
import logoCbt from '@/assets/logo-cbt-new2.png';
import CaseStudies from '@/components/CaseStudies';
const LandingPage = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const toggleVideo = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  return <div className="min-h-screen bg-white text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-white/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={logo} alt="BoxClose" className="h-10" />
          </Link>
          <Link to="/book" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Book a Demo
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          {/* Step indicator */}
          <p className="font-light text-sm md:text-base mb-4 tracking-wide text-primary">STEP 1 OF 2: WATCH THE TRAINING BELOW</p>
          
          {/* Headline */}
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-muted-foreground mb-6 leading-tight">
            Skip the Sales Floor Line.<br className="hidden md:block" /> Step Into the <span className="text-primary font-bold">Finance Office</span>.
          </h1>
          
          {/* Subheadline */}
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto mb-4 leading-relaxed">
            The BoxClose Method shows you how to become an F&I manager in <span className="font-bold">6 months or less</span>—and earn a consistent <span className="font-bold">$15K–$20K</span> per month, without having to live paycheck to paycheck or guess your way through the box.
          </p>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">Additionally stop relying on unstable commissions, desk decisions, and unpredictable traffic to determine your income.</p>

          {/* Step Indicator */}
          

          {/* Video Section */}
          <div className="relative bg-muted border border-border rounded-lg overflow-hidden mb-10 aspect-video max-w-3xl mx-auto shadow-lg">
            <video ref={videoRef} className="w-full h-full object-cover" poster="/placeholder.svg" playsInline>
              <source src="" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            
            {/* Play Button Overlay */}
            <button onClick={toggleVideo} className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/20 transition-colors group">
              <div className="w-20 h-20 rounded-full bg-white/20 border border-white/30 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                {isPlaying ? <Pause className="w-8 h-8 text-white" /> : <Play className="w-8 h-8 text-white ml-1" />}
              </div>
            </button>
          </div>

          {/* Step 2 Indicator */}
          

          {/* Primary CTA */}
          <Link to="/book" className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors text-lg shadow-md">STEP 2 OF 2: SCHEDULE FREE CONSULTATION NOW </Link>

        </div>
      </section>

      {/* As Seen On - Full Width */}
      <section className="w-full bg-slate-900 py-10">
        <div className="container mx-auto px-6">
          <p className="text-center text-white/60 text-xs uppercase tracking-widest mb-6">
            As Seen On
          </p>
          <div className="flex items-center justify-center gap-8 md:gap-16 flex-wrap">
            <img src={logoFoxBusiness} alt="Fox Business" className="h-8 md:h-10 object-contain" />
            <img src={logoCnbc} alt="CNBC" className="h-8 md:h-10 object-contain" />
            <img src={logoOnbe} alt="Onbe" className="h-8 md:h-10 object-contain" />
            <img src={logoAfn} alt="AFN" className="h-8 md:h-10 object-contain" />
            <img src={logoCbt} alt="CBT News" className="h-8 md:h-10 object-contain" />
          </div>
        </div>
      </section>
      {/* Case Studies */}
      <CaseStudies />

      {/* About Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-3xl">
          <h2 className="font-serif text-3xl md:text-4xl text-center mb-10 text-[#0e172a]">
            About BoxClose
          </h2>
          
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-8 md:p-12">
            <div className="space-y-6 text-white/70 leading-relaxed">
              <p>
                BoxClose exists because F&I should be built on structure, not shortcuts. We provide professional training for individuals in automotive finance and insurance who understand that career longevity comes from doing things correctly—not from high-pressure tactics.
              </p>
              <p>
                Our approach focuses on what actually protects your career: compliance fundamentals, proper funding procedures, lender relationships, and the discipline to manage risk in every deal. This isn't motivational content or sales coaching. It's the operational foundation that separates professionals from those who burn out.
              </p>
              <p>
                We work with salespeople transitioning into F&I, new managers building their foundation, and experienced professionals who want to refine their process. The goal is simple—help you become the F&I professional that lenders trust and dealerships rely on.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-white mb-6">
            Ready to Build a Professional Foundation?
          </h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Schedule a conversation to discuss your situation and goals.
          </p>
          <Link to="/book" className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors shadow-md">
            Book a Demo
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-white border-t border-border">
        <div className="container mx-auto px-6 text-center">
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} BoxClose. All rights reserved.
          </p>
        </div>
      </footer>
    </div>;
};
export default LandingPage;